import { useEffect, useState } from "react";
import {
  CalendarDaysIcon,
  ClockIcon,
  UserIcon,
  CheckIcon,
  ChevronDownIcon,
  ShoppingCartIcon,
  ShieldCheckIcon,
} from "@heroicons/react/24/outline";
import Layout from "./layout";
import { useParams } from "react-router-dom";
import { useCoursesContext } from "../context/courses_context";
import { Link } from "react-router-dom";
import { useCartContext } from "../context/cart_context";
import { Oval } from "react-loader-spinner";
import parse from "html-react-parser";

const SECTION_THEMES = [
  {
    wrap: "bg-gradient-to-br from-teal-50/90 to-white border-teal-100",
    number: "bg-teal-700 text-white",
    label: "text-teal-700",
    bar: "from-teal-500 to-teal-300",
  },
  {
    wrap: "bg-gradient-to-br from-sky-50/90 to-white border-sky-100",
    number: "bg-sky-700 text-white",
    label: "text-sky-700",
    bar: "from-sky-500 to-sky-300",
  },
  {
    wrap: "bg-gradient-to-br from-emerald-50/90 to-white border-emerald-100",
    number: "bg-emerald-700 text-white",
    label: "text-emerald-700",
    bar: "from-emerald-500 to-emerald-300",
  },
  {
    wrap: "bg-gradient-to-br from-slate-50 to-white border-slate-200",
    number: "bg-slate-800 text-white",
    label: "text-slate-700",
    bar: "from-slate-600 to-slate-300",
  },
  {
    wrap: "bg-gradient-to-br from-cyan-50/90 to-white border-cyan-100",
    number: "bg-cyan-800 text-white",
    label: "text-cyan-800",
    bar: "from-cyan-500 to-cyan-300",
  },
  {
    wrap: "bg-gradient-to-br from-indigo-50/80 to-white border-indigo-100",
    number: "bg-indigo-800 text-white",
    label: "text-indigo-800",
    bar: "from-indigo-500 to-indigo-300",
  },
  {
    wrap: "bg-gradient-to-br from-stone-50 to-white border-stone-200",
    number: "bg-stone-800 text-white",
    label: "text-stone-700",
    bar: "from-stone-500 to-stone-300",
  },
];

const ContentSection = ({ title, children, index = 0 }) => {
  if (!children) return null;
  const theme = SECTION_THEMES[index % SECTION_THEMES.length];
  const step = String(index + 1).padStart(2, "0");

  return (
    <section
      className={`scroll-mt-28 overflow-hidden rounded-2xl border ${theme.wrap} p-5 shadow-sm sm:p-6`}
    >
      <div className="mb-4 flex items-start gap-3.5">
        <span
          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-xs font-bold tracking-wider shadow-sm ${theme.number}`}
        >
          {step}
        </span>
        <div className="min-w-0 flex-1 pt-0.5">
          <p
            className={`text-[11px] font-semibold uppercase tracking-[0.16em] ${theme.label}`}
          >
            Section {step}
          </p>
          <h2 className="mt-0.5 text-lg font-semibold tracking-tight text-slate-900 md:text-xl">
            {title}
          </h2>
          <span
            className={`mt-2.5 block h-0.5 w-16 rounded-full bg-gradient-to-r ${theme.bar}`}
            aria-hidden
          />
        </div>
      </div>
      <div className="training-prose text-[15px] leading-relaxed text-slate-600">
        {children}
      </div>
    </section>
  );
};

const PricingOption = ({ pricing, isChecked, onToggle }) => (
  <label
    className={`group flex cursor-pointer items-start gap-3 rounded-xl border px-3.5 py-3.5 transition-all duration-200 ${
      isChecked
        ? "border-teal-500 bg-gradient-to-r from-teal-50 to-cyan-50/60 shadow-md shadow-teal-500/10 ring-1 ring-teal-500/25"
        : "border-slate-200/90 bg-white/90 hover:border-teal-300/60 hover:bg-slate-50/80 hover:shadow-sm"
    }`}
  >
    <span
      className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border transition-colors ${
        isChecked
          ? "border-teal-600 bg-teal-600 text-white shadow-sm"
          : "border-slate-300 bg-white group-hover:border-teal-400"
      }`}
    >
      {isChecked ? (
        <CheckIcon className="h-3.5 w-3.5" strokeWidth={2.5} />
      ) : null}
      <input
        type="checkbox"
        className="sr-only"
        checked={isChecked}
        onChange={onToggle}
      />
    </span>
    <span className="flex min-w-0 flex-1 items-start justify-between gap-3">
      <span
        className={`text-sm leading-snug ${
          isChecked ? "font-medium text-slate-900" : "text-slate-700"
        }`}
      >
        {pricing.sessionType}
      </span>
      <span
        className={`shrink-0 text-sm font-semibold tabular-nums ${
          isChecked ? "text-teal-700" : "text-slate-900"
        }`}
      >
        ${pricing.price}
      </span>
    </span>
  </label>
);

const PricingGroup = ({ title, children }) => (
  <div>
    <h3 className="mb-2.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
      {title}
    </h3>
    <div className="space-y-2">{children}</div>
  </div>
);

const SingleTrainingDetail = () => {
  const { id } = useParams();
  const { fetchSingleCourse, single_course } = useCoursesContext();
  const { addToCart } = useCartContext();
  const [loading, setLoading] = useState(true);
  const [selectedPricings, setSelectedPricings] = useState([]);
  const [showMore, setShowMore] = useState(false);

  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      await fetchSingleCourse(id);
      setLoading(false);
    };
    fetchData();
  }, [id, fetchSingleCourse]);

  useEffect(() => {
    if (single_course?.Pricings?.length) {
      const isPastWebinar = new Date(single_course.webinarDate) < new Date();

      if (isPastWebinar) {
        const accessOptions = single_course.Pricings.filter(
          (pricing) =>
            pricing.sessionType === "Recorded session" ||
            pricing.sessionType === "Transcript" ||
            pricing.sessionType === "Recorded Plus Transcript session",
        );

        if (accessOptions.length > 0) {
          setSelectedPricings([accessOptions[0]]);
        }
      } else {
        setSelectedPricings([single_course.Pricings[0]]);
      }
    }
  }, [single_course]);

  const handlePricingToggle = (pricing) => {
    setSelectedPricings((prev) => {
      const exists = prev.find((p) => p.id === pricing.id);

      if (exists) {
        return prev.filter((p) => p.id !== pricing.id);
      } else {
        return [...prev, pricing];
      }
    });
  };

  const totalPrice = selectedPricings.reduce(
    (sum, item) => sum + parseFloat(item.price),
    0,
  );

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-slate-100">
        <Oval
          height={50}
          width={50}
          color="#0f766e"
          wrapperStyle={{}}
          wrapperClass=""
          visible={true}
          ariaLabel="oval-loading"
          secondaryColor="#99f6e4"
          strokeWidth={2}
          strokeWidthSecondary={2}
        />
      </div>
    );
  }

  const {
    courseID,
    title,
    instructor,
    discountedPrice,
    description,
    what_you_will_learn,
    imageSrc,
    Pricings = [],
    webinarDate,
    duration,
    areas_covered,
    who_will_benefit,
    instructor_profile,
    why_register,
    background,
  } = single_course;

  const dateTime = new Date(webinarDate);
  const webinarDateUTC = new Date(webinarDate);
  const isPastWebinar = new Date(webinarDate) < new Date();
  const accessOptions = Pricings.filter(
    (pricing) =>
      pricing.sessionType === "Recorded session" ||
      pricing.sessionType === "Transcript" ||
      pricing.sessionType === "Recorded Plus Transcript session",
  );

  const day = webinarDateUTC.getUTCDate();
  const monthYear = webinarDateUTC.toLocaleString("en-US", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
  const weekday = webinarDateUTC.toLocaleString("en-US", {
    weekday: "long",
    timeZone: "UTC",
  });
  const formattedTimeEST = dateTime.toLocaleString("en-US", {
    timeZone: "America/New_York",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

  const formattedTimePST = dateTime.toLocaleString("en-US", {
    timeZone: "America/Los_Angeles",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

  function convertMinutes(minutes) {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    if (minutes <= 60) {
      return `${minutes} min`;
    }

    return `${hours} hour${hours > 1 ? "s" : ""} ${remainingMinutes} min`;
  }

  const visiblePricings = showMore
    ? Pricings.slice(0, 5)
    : Pricings.slice(0, 2);

  const savings = selectedPricings.length * 49;
  const wasPrice =
    totalPrice != null && totalPrice > 0
      ? (totalPrice + savings).toFixed(2)
      : "00.00";
  const displayPrice =
    totalPrice != null && totalPrice > 0 ? totalPrice.toFixed(2) : "00.00";

  const handleAddToCart = (e) => {
    if (selectedPricings.length === 0) {
      e.preventDefault();
      return;
    }
    addToCart(
      courseID,
      imageSrc,
      title,
      instructor,
      selectedPricings.length > 0 ? totalPrice : discountedPrice,
      selectedPricings,
    );
  };

  const instructorName = instructor?.replace(/"/g, "") || "Expert Instructor";
  const monthShort = monthYear.split(" ")[0].slice(0, 3);

  const PriceSummary = () => (
    <div className="text-center">
      <p className="text-xs font-medium uppercase tracking-wider text-slate-400">
        Total
      </p>
      <div className="mt-1 flex items-baseline justify-center gap-2.5">
        <span className="text-sm text-slate-400 line-through">
          Was ${wasPrice}
        </span>
        <span className="text-4xl font-bold tracking-tight text-slate-900 tabular-nums">
          ${displayPrice}
        </span>
      </div>
      {savings > 0 ? (
        <p className="mt-2 inline-flex items-center rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700 ring-1 ring-emerald-200/70">
          You save ${savings}
        </p>
      ) : null}
    </div>
  );

  const AddToCartButton = () => (
    <Link
      to={selectedPricings.length === 0 ? "#" : "/cart"}
      onClick={handleAddToCart}
      className={`mt-5 flex w-full items-center justify-center gap-2 rounded-xl px-4 py-3.5 text-sm font-semibold tracking-wide transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 ${
        selectedPricings.length === 0
          ? "pointer-events-none cursor-not-allowed bg-slate-200 text-slate-400"
          : "bg-gradient-to-r from-teal-700 to-cyan-700 text-white shadow-lg shadow-teal-700/30 hover:from-teal-800 hover:to-cyan-800 hover:shadow-xl hover:shadow-teal-700/35 active:scale-[0.99]"
      }`}
    >
      <ShoppingCartIcon className="h-5 w-5" />
      Add to Cart
    </Link>
  );

  const EnrollmentPanel = ({ children }) => (
    <div className="overflow-hidden rounded-2xl border border-white/70 bg-white/90 shadow-2xl shadow-slate-300/40 ring-1 ring-slate-200/60 backdrop-blur-md">
      <div className="relative border-b border-slate-100 px-5 py-6">
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-teal-50/80 via-white to-cyan-50/50" />
        <div className="relative">
          <PriceSummary />
          <AddToCartButton />
          {selectedPricings.length > 0 && !isPastWebinar ? (
            <p className="mt-3 text-center text-xs text-slate-400">
              {selectedPricings.length} option
              {selectedPricings.length > 1 ? "s" : ""} selected
            </p>
          ) : null}
        </div>
      </div>
      <div className="px-5 py-5">{children}</div>
    </div>
  );

  return (
    <Layout>
      <div className="relative min-h-screen overflow-hidden">
        {/* Clean page background */}
        <div className="pointer-events-none absolute inset-0 -z-10">
          <div className="absolute inset-0 bg-[#eef1f4]" />
          <div className="absolute inset-0 bg-gradient-to-b from-white/70 via-transparent to-slate-200/50" />
          <div
            className="absolute inset-0 opacity-[0.45]"
            style={{
              backgroundImage:
                "repeating-linear-gradient(-12deg, transparent, transparent 42px, rgba(15, 23, 42, 0.03) 42px, rgba(15, 23, 42, 0.03) 43px)",
            }}
          />
        </div>

        {/* Hero */}
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-[#0b2a33] to-[#0f3d42]" />
          <div
            className="absolute inset-0 opacity-30"
            style={{
              backgroundImage: `
                radial-gradient(circle at 20% 20%, rgba(45, 212, 191, 0.35), transparent 40%),
                radial-gradient(circle at 80% 10%, rgba(56, 189, 248, 0.28), transparent 35%),
                radial-gradient(circle at 70% 80%, rgba(20, 184, 166, 0.22), transparent 40%)
              `,
            }}
          />
          <div
            className="absolute inset-0 opacity-[0.12]"
            style={{
              backgroundImage:
                "linear-gradient(rgba(255,255,255,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.08) 1px, transparent 1px)",
              backgroundSize: "48px 48px",
            }}
          />

          <div className="relative mx-auto max-w-7xl px-4 py-10 sm:px-6 md:py-12 lg:px-8 lg:py-14">
            <div className="grid items-center gap-8 lg:grid-cols-12 lg:gap-10">
              {/* Featured image — full image, slightly smaller */}
              <div className="order-1 lg:order-2 lg:col-span-4">
                <div className="relative mx-auto w-full max-w-[240px] sm:max-w-[260px] lg:max-w-[280px]">
                  <div className="absolute -inset-2 rounded-2xl bg-gradient-to-br from-teal-400/30 via-cyan-300/15 to-transparent blur-lg" />
                  <div className="relative overflow-hidden rounded-2xl border border-white/20 bg-gradient-to-b from-white/15 to-white/5 p-2.5 shadow-2xl shadow-black/40 backdrop-blur-sm">
                    <div className="flex min-h-[160px] items-center justify-center rounded-xl bg-gradient-to-br from-slate-900/40 via-teal-950/30 to-slate-900/50 sm:min-h-[180px]">
                      <img
                        src={imageSrc}
                        alt={title}
                        className="max-h-[200px] w-full object-contain drop-shadow-xl sm:max-h-[220px] md:max-h-[240px]"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Title + meta */}
              <div className="order-2 lg:order-1 lg:col-span-8">
                <p className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-teal-300">
                  {isPastWebinar ? "On-demand training" : "Professional webinar"}
                </p>

                <h1 className="text-2xl font-bold leading-tight tracking-tight text-white sm:text-3xl md:text-[2.35rem] md:leading-[1.2]">
                  {title}
                </h1>

                <div className="mt-7 flex flex-wrap gap-3">
                  <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3.5 py-2 text-sm text-slate-100 backdrop-blur-sm">
                    <UserIcon className="h-4 w-4 text-teal-300" />
                    {instructorName}
                  </div>
                  {duration ? (
                    <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3.5 py-2 text-sm text-slate-100 backdrop-blur-sm">
                      <ClockIcon className="h-4 w-4 text-teal-300" />
                      {convertMinutes(duration)}
                    </div>
                  ) : null}
                  <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3.5 py-2 text-sm text-slate-100 backdrop-blur-sm">
                    <CalendarDaysIcon className="h-4 w-4 text-teal-300" />
                    {weekday}, {monthShort} {day}
                  </div>
                </div>

                <p className="mt-6 max-w-xl text-sm leading-relaxed text-slate-300/90">
                  {formattedTimeEST} EST · {formattedTimePST} PST
                </p>
              </div>
            </div>
          </div>

          {/* Soft fade into page */}
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-[#eef1f4] to-transparent" />
        </section>

        {/* Schedule strip */}
        <div className="relative z-10 mx-auto -mt-6 max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 overflow-hidden rounded-2xl border border-white/80 bg-white/85 shadow-xl shadow-teal-900/5 backdrop-blur-md sm:grid-cols-3">
            <div className="flex items-center gap-4 border-b border-slate-100 px-5 py-5 sm:border-b-0 sm:border-r">
              <div className="flex h-14 w-14 flex-col items-center justify-center rounded-2xl bg-gradient-to-br from-teal-500 to-cyan-600 text-white shadow-lg shadow-teal-500/25">
                <span className="text-[10px] font-semibold uppercase leading-none tracking-wider text-teal-50">
                  {monthShort}
                </span>
                <span className="text-xl font-bold leading-none">{day}</span>
              </div>
              <div>
                <p className="text-xs font-medium uppercase tracking-wider text-slate-400">
                  Session date
                </p>
                <p className="mt-0.5 text-sm font-semibold text-slate-900">
                  {weekday}
                </p>
                <p className="text-sm text-slate-500">{monthYear}</p>
              </div>
            </div>

            <div className="flex items-center gap-4 border-b border-slate-100 px-5 py-5 sm:border-b-0 sm:border-r">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100 text-slate-700">
                <ClockIcon className="h-6 w-6" />
              </div>
              <div>
                <p className="text-xs font-medium uppercase tracking-wider text-slate-400">
                  Time zones
                </p>
                <p className="mt-0.5 text-sm font-semibold text-slate-900">
                  {formattedTimeEST} EST
                </p>
                <p className="text-sm text-slate-500">{formattedTimePST} PST</p>
              </div>
            </div>

            <div className="flex items-center gap-4 px-5 py-5">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100 text-slate-700">
                <UserIcon className="h-6 w-6" />
              </div>
              <div>
                <p className="text-xs font-medium uppercase tracking-wider text-slate-400">
                  Presented by
                </p>
                <p className="mt-0.5 text-sm font-semibold text-slate-900">
                  {instructorName}
                </p>
                {duration ? (
                  <p className="text-sm text-slate-500">
                    {convertMinutes(duration)} session
                  </p>
                ) : null}
              </div>
            </div>
          </div>
        </div>

        {/* Main content */}
        <div className="relative mx-auto max-w-7xl px-4 py-10 sm:px-6 md:py-14 lg:px-8">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-10">
            <div className="space-y-4 lg:col-span-8">
              {description ? (
                <ContentSection title="Description" index={0}>
                  {parse(description)}
                </ContentSection>
              ) : null}

              {why_register ? (
                <ContentSection title="Why Register" index={1}>
                  {parse(why_register)}
                </ContentSection>
              ) : null}

              {what_you_will_learn ? (
                <ContentSection title="Why Should You Attend" index={2}>
                  {parse(what_you_will_learn)}
                </ContentSection>
              ) : null}

              {areas_covered ? (
                <ContentSection
                  title="Areas Covered in the Webinar Session"
                  index={3}
                >
                  {parse(areas_covered)}
                </ContentSection>
              ) : null}

              {who_will_benefit ? (
                <ContentSection title="Who Will Benefit" index={4}>
                  {parse(who_will_benefit)}
                </ContentSection>
              ) : null}

              {instructor_profile ? (
                <ContentSection title="Instructor Profile" index={5}>
                  {parse(instructor_profile)}
                </ContentSection>
              ) : null}

              {background ? (
                <ContentSection title="Background" index={6}>
                  {parse(background)}
                </ContentSection>
              ) : null}
            </div>

            <aside className="lg:col-span-4">
              <div className="sticky top-6 space-y-4">
                {isPastWebinar ? (
                  <EnrollmentPanel>
                    <div className="space-y-4">
                      <PricingGroup title="Access Options">
                        {accessOptions.map((pricing) => {
                          const isChecked = selectedPricings.some(
                            (p) => p.id === pricing.id,
                          );
                          return (
                            <PricingOption
                              key={pricing.id}
                              pricing={pricing}
                              isChecked={isChecked}
                              onToggle={() => handlePricingToggle(pricing)}
                            />
                          );
                        })}
                      </PricingGroup>
                    </div>
                  </EnrollmentPanel>
                ) : (
                  <EnrollmentPanel>
                    <div className="max-h-[calc(100vh-16rem)] space-y-6 overflow-y-auto pr-0.5">
                      <PricingGroup title="Live Webinar">
                        {visiblePricings.map((pricing) => {
                          const isChecked = selectedPricings.some(
                            (p) => p.id === pricing.id,
                          );
                          return (
                            <PricingOption
                              key={pricing.id}
                              pricing={pricing}
                              isChecked={isChecked}
                              onToggle={() => handlePricingToggle(pricing)}
                            />
                          );
                        })}

                        {Pricings.length > 2 ? (
                          <button
                            type="button"
                            onClick={() => setShowMore(!showMore)}
                            className="mt-1 flex w-full items-center justify-center gap-1.5 rounded-lg py-2 text-sm font-medium text-teal-700 transition-colors hover:bg-teal-50"
                          >
                            {showMore ? "Less Attendees" : "More Attendees"}
                            <ChevronDownIcon
                              className={`h-4 w-4 transition-transform duration-200 ${
                                showMore ? "rotate-180" : ""
                              }`}
                            />
                          </button>
                        ) : null}
                      </PricingGroup>

                      <PricingGroup title="On-Demand">
                        {Pricings?.filter(
                          (pricing) =>
                            pricing.sessionType === "Recorded session" ||
                            pricing.sessionType === "Transcript",
                        ).map((pricing) => {
                          const isChecked = selectedPricings.some(
                            (p) => p.id === pricing.id,
                          );
                          return (
                            <PricingOption
                              key={pricing.id}
                              pricing={pricing}
                              isChecked={isChecked}
                              onToggle={() => handlePricingToggle(pricing)}
                            />
                          );
                        })}
                      </PricingGroup>

                      <PricingGroup title="Value Packs">
                        {Pricings?.filter(
                          (pricing) =>
                            pricing.sessionType ===
                              "Live Plus Recorded session" ||
                            pricing.sessionType ===
                              "Live Plus Transcript session" ||
                            pricing.sessionType ===
                              "Recorded Plus Transcript session" ||
                            pricing.sessionType ===
                              "Group Session For 10 Attendees" ||
                            pricing.sessionType ===
                              "Group Session For More Than 10 Attendees",
                        ).map((pricing) => {
                          const isChecked = selectedPricings.some(
                            (p) => p.id === pricing.id,
                          );
                          return (
                            <PricingOption
                              key={pricing.id}
                              pricing={pricing}
                              isChecked={isChecked}
                              onToggle={() => handlePricingToggle(pricing)}
                            />
                          );
                        })}
                      </PricingGroup>
                    </div>
                  </EnrollmentPanel>
                )}

                <div className="flex items-start gap-2.5 rounded-xl border border-teal-100/80 bg-white/70 px-3.5 py-3 text-xs leading-relaxed text-slate-500 shadow-sm backdrop-blur-sm">
                  <ShieldCheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-teal-600" />
                  <p>
                    Secure checkout. Access details are delivered by email after
                    purchase.
                  </p>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </div>

      <style>{`
        .training-prose p {
          margin-bottom: 0.85rem;
        }
        .training-prose ul,
        .training-prose ol {
          margin: 0.75rem 0 0.85rem;
          padding-left: 1.25rem;
        }
        .training-prose ul {
          list-style: disc;
        }
        .training-prose ol {
          list-style: decimal;
        }
        .training-prose li {
          margin-bottom: 0.35rem;
        }
        .training-prose strong,
        .training-prose b {
          color: #0f172a;
          font-weight: 600;
        }
        .training-prose a {
          color: #0f766e;
          text-decoration: underline;
          text-underline-offset: 2px;
        }
        .training-prose h1,
        .training-prose h2,
        .training-prose h3,
        .training-prose h4 {
          color: #0f172a;
          font-weight: 600;
          margin: 1rem 0 0.5rem;
        }
      `}</style>
    </Layout>
  );
};

export default SingleTrainingDetail;
